import React, { useState, useEffect, useRef, useCallback, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCmsData, saveCmsData, resetCmsData, getCmsImagePath } from '../cms/cmsData';
import RichTextEditor from '../components/RichTextEditor';
import api from '../services/api';
import './CmsDashboard.css';

const FaqItem = memo(({ item, itemIndex, sectionIndex, totalItems, onCommit, onRemove, onMove }) => {
  const [q, setQ] = useState(item.question);
  const [a, setA] = useState(item.answer);

  useEffect(() => { setQ(item.question); }, [item.question]);
  useEffect(() => { setA(item.answer); }, [item.answer]);

  const btnStyle = { background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', padding: '2px 6px', fontSize: '0.75rem', color: '#6b7280' };

  return (
    <div className="cms-faq-item" style={{ position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
        <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>Q{itemIndex + 1}</span>
        <div style={{ display: 'flex', gap: '4px' }}>
          {itemIndex > 0 && <button style={btnStyle} onClick={() => onMove(sectionIndex, itemIndex, -1)} title="Move up">&#9650;</button>}
          {itemIndex < totalItems - 1 && <button style={btnStyle} onClick={() => onMove(sectionIndex, itemIndex, 1)} title="Move down">&#9660;</button>}
          <button style={{ ...btnStyle, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => onRemove(sectionIndex, itemIndex)} title="Remove item">&#10005;</button>
        </div>
      </div>
      <div className="cms-field-group">
        <label>Question</label>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onBlur={() => onCommit(sectionIndex, itemIndex, 'question', q)}
        />
      </div>
      <div className="cms-field-group">
        <label>Answer</label>
        <textarea
          value={a}
          onChange={(e) => setA(e.target.value)}
          onBlur={() => onCommit(sectionIndex, itemIndex, 'answer', a)}
          rows={3}
        />
      </div>
    </div>
  );
});

const FaqSectionBlock = memo(({ section, sectionIndex, totalSections, onSubheadingCommit, onItemCommit, onAddItem, onRemoveItem, onMoveItem, onRemoveSection, onMoveSection }) => {
  const [heading, setHeading] = useState(section.subheading);

  useEffect(() => { setHeading(section.subheading); }, [section.subheading]);

  const sectionBtnStyle = { background: 'none', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', padding: '3px 8px', fontSize: '0.8rem', color: '#6b7280' };

  return (
    <div className="cms-faq-section" style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '1rem', marginBottom: '1rem', background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <h3 className="cms-subsection-title" style={{ margin: 0 }}>{section.subheading}</h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          {sectionIndex > 0 && <button style={sectionBtnStyle} onClick={() => onMoveSection(sectionIndex, -1)} title="Move section up">&#9650;</button>}
          {sectionIndex < totalSections - 1 && <button style={sectionBtnStyle} onClick={() => onMoveSection(sectionIndex, 1)} title="Move section down">&#9660;</button>}
          <button style={{ ...sectionBtnStyle, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => onRemoveSection(sectionIndex)} title="Remove section">&#10005;</button>
        </div>
      </div>
      <div className="cms-field-group">
        <label>Section Heading</label>
        <input
          type="text"
          value={heading}
          onChange={(e) => setHeading(e.target.value)}
          onBlur={() => onSubheadingCommit(sectionIndex, heading)}
        />
      </div>
      {section.items.map((item, itemIndex) => (
        <FaqItem
          key={item.id}
          item={item}
          itemIndex={itemIndex}
          sectionIndex={sectionIndex}
          totalItems={section.items.length}
          onCommit={onItemCommit}
          onRemove={onRemoveItem}
          onMove={onMoveItem}
        />
      ))}
      <button
        onClick={() => onAddItem(sectionIndex)}
        style={{ marginTop: '0.5rem', padding: '0.4rem 0.8rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82rem', fontWeight: '500' }}
      >
        + Add Question
      </button>
    </div>
  );
});

const CmsDashboard = () => {
  const navigate = useNavigate();
  const [cmsData, setCmsData] = useState(getCmsData());
  const [activeSection, setActiveSection] = useState('global');
  const [saveStatus, setSaveStatus] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // Check if admin is authenticated
    const adminSession = sessionStorage.getItem('cmsAdmin');
    if (!adminSession) {
      navigate('/cms-login');
      return;
    }

    try {
      const session = JSON.parse(adminSession);
      if (!session.isAuthenticated) {
        navigate('/cms-login');
      }
    } catch (e) {
      navigate('/cms-login');
    }
  }, [navigate]);

  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageUploadTarget, setImageUploadTarget] = useState(null);

  const handleImageUpload = (section, field, nestedField = null, index = null) => {
    setImageUploadTarget({ section, field, nestedField, index });
    fileInputRef.current?.click();
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !imageUploadTarget) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setSaveStatus('Error: Please select an image file');
      setTimeout(() => setSaveStatus(''), 3000);
      return;
    }

    setUploadingImage(true);
    try {
      // Try to upload via API first
      let imageUrl = null;
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('type', 'cms_image');
        const response = await api.request('/config/upload-image/', {
          method: 'POST',
          body: formData,
          headers: {} // Let browser set content-type with boundary
        });
        if (response?.success && response.data?.url) {
          imageUrl = response.data.url;
        }
      } catch (err) {
        // Fallback: use data URL for local storage
        console.log('API upload not available, using local data URL');
      }

      if (!imageUrl) {
        // Fallback: convert to data URL (works with localStorage CMS)
        imageUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      const { section, field, nestedField, index } = imageUploadTarget;
      if (section === 'siteImages') {
        // Site images are objects with path/label/usedOn
        const newImages = { ...cmsData.siteImages };
        newImages[field] = { ...newImages[field], path: imageUrl };
        handleInputChange('siteImages', field, newImages[field]);
      } else {
        handleInputChange(section, field, imageUrl, nestedField, index);
      }
      setSaveStatus('Image uploaded! Remember to save.');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (err) {
      console.error('Image upload error:', err);
      setSaveStatus('Error uploading image');
      setTimeout(() => setSaveStatus(''), 3000);
    } finally {
      setUploadingImage(false);
      setImageUploadTarget(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('cmsAdmin');
    navigate('/cms-login');
  };

  const handleInputChange = (section, field, value, nestedField = null, index = null) => {
    setCmsData(prev => {
      const newData = { ...prev };
      
      if (nestedField !== null && index !== null) {
        // Handle nested array items (like FAQ items)
        newData[section] = { ...newData[section] };
        newData[section][field] = [...newData[section][field]];
        newData[section][field][index] = { ...newData[section][field][index], [nestedField]: value };
      } else if (nestedField !== null) {
        // Handle nested objects
        newData[section] = { ...newData[section] };
        newData[section][field] = { ...newData[section][field], [nestedField]: value };
      } else if (Array.isArray(newData[section][field])) {
        // Handle arrays
        newData[section] = { ...newData[section] };
        newData[section][field] = value;
      } else {
        // Handle simple fields
        newData[section] = { ...newData[section], [field]: value };
      }
      
      return newData;
    });
    setHasChanges(true);
  };

  const handleArrayItemChange = (section, field, index, subField, value) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData[section] = { ...newData[section] };
      newData[section][field] = [...newData[section][field]];
      newData[section][field][index] = { ...newData[section][field][index], [subField]: value };
      return newData;
    });
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaveStatus('Saving...');
    await saveCmsData(cmsData);
    // Force storage event for cross-tab/component refresh
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cmsData',
      newValue: JSON.stringify(cmsData)
    }));
    setSaveStatus('Changes saved successfully!');
    setHasChanges(false);
    setTimeout(() => setSaveStatus(''), 3000);
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all content to defaults? This cannot be undone.')) {
      const defaultData = await resetCmsData();
      setCmsData(defaultData);
      setSaveStatus('Content reset to defaults');
      setHasChanges(false);
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const sections = [
    { id: 'global', label: 'Global Settings', icon: '⚙️' },
    { id: 'header', label: 'Header / Navigation', icon: '📍' },
    { id: 'homePage', label: 'Home Page', icon: '🏠' },
    { id: 'aboutPage', label: 'About Page', icon: '📄' },
    { id: 'whatIsMenaPage', label: 'What is MENA', icon: '🌍' },
    { id: 'faqPage', label: 'FAQ Page', icon: '❓' },
    { id: 'ticketsPage', label: 'Tickets Page', icon: '🎟️' },
    { id: 'amphitheaterPage', label: 'Amphitheater Page', icon: '🎭' },
    { id: 'contactPage', label: 'Contact Page', icon: '📧' },
    { id: 'festivalInfoPage', label: 'Festival Info', icon: '📋' },
    { id: 'sponsorsPage', label: 'Sponsors Page', icon: '🤝' },
    { id: 'vendorsPage', label: 'Vendors Page', icon: '🏪' },
    { id: 'eventSchedulePage', label: 'Event Schedule', icon: '📅' },
    { id: 'eventMapPage', label: 'Event Map', icon: '🗺️' },
    { id: 'mapsDirectionsPage', label: 'Maps & Directions', icon: '📍' },
    { id: 'loginPage', label: 'Login Page', icon: '🔑' },
    { id: 'signupPage', label: 'Signup Page', icon: '📝' },
    { id: 'vendorBoothsPage', label: 'Vendor Booths', icon: '🏪' },
    { id: 'musicPage', label: 'Music Page', icon: '🎵' },
    { id: 'legalPages', label: 'Legal Pages', icon: '⚖️' },
    { id: 'footer', label: 'Footer', icon: '🦶' },
    { id: 'siteImages', label: 'Site Images', icon: '🖼️' },
    { id: 'seoMetadata', label: 'SEO & Share Images', icon: '🔍' },
    { id: 'customCSS', label: 'Custom CSS', icon: '🎨' },
  ];

  const renderGlobalSettings = () => (
    <div className="cms-section-content">
      <h2>Global Settings</h2>
      <div className="cms-field-group">
        <label>Site Name</label>
        <RichTextEditor
          value={cmsData.global.siteName}
          onChange={(val) => handleInputChange('global', 'siteName', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Site Tagline</label>
        <RichTextEditor
          value={cmsData.global.siteTagline}
          onChange={(val) => handleInputChange('global', 'siteTagline', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Event Date</label>
        <input
          type="text"
          value={cmsData.global.eventDate}
          onChange={(e) => handleInputChange('global', 'eventDate', e.target.value)}
        />
      </div>
      <div className="cms-field-group">
        <label>Event Location</label>
        <RichTextEditor
          value={cmsData.global.eventLocation}
          onChange={(val) => handleInputChange('global', 'eventLocation', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Contact Email</label>
        <input
          type="email"
          value={cmsData.global.contactEmail}
          onChange={(e) => handleInputChange('global', 'contactEmail', e.target.value)}
        />
      </div>
      <div className="cms-field-group">
        <label>Copyright Text</label>
        <RichTextEditor
          value={cmsData.global.copyrightText}
          onChange={(val) => handleInputChange('global', 'copyrightText', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Announcement Bar Text</label>
        <RichTextEditor
          value={cmsData.global.announcementBar}
          onChange={(val) => handleInputChange('global', 'announcementBar', val)}
        />
      </div>
    </div>
  );

  const renderHomePage = () => (
    <div className="cms-section-content">
      <h2>Home Page</h2>
      <div className="cms-field-group">
        <label>Hero Title</label>
        <RichTextEditor
          value={cmsData.homePage.heroTitle}
          onChange={(val) => handleInputChange('homePage', 'heroTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Hero Subtitle</label>
        <RichTextEditor
          value={cmsData.homePage.heroSubtitle}
          onChange={(val) => handleInputChange('homePage', 'heroSubtitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Hero Description</label>
        <RichTextEditor
          value={cmsData.homePage.heroDescription}
          onChange={(val) => handleInputChange('homePage', 'heroDescription', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Countdown Target Date</label>
        <input
          type="text"
          value={cmsData.homePage.countdownTargetDate}
          onChange={(e) => handleInputChange('homePage', 'countdownTargetDate', e.target.value)}
          placeholder="e.g., June 19, 2026 00:00:00"
        />
      </div>
      <div className="cms-field-group">
        <label>Welcome Title</label>
        <RichTextEditor
          value={cmsData.homePage.welcomeTitle}
          onChange={(val) => handleInputChange('homePage', 'welcomeTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Welcome Text</label>
        <RichTextEditor
          value={cmsData.homePage.welcomeText}
          onChange={(val) => handleInputChange('homePage', 'welcomeText', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Vendor Button Text</label>
        <RichTextEditor
          value={cmsData.homePage.ctaVendorButton}
          onChange={(val) => handleInputChange('homePage', 'ctaVendorButton', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Tickets Button Text</label>
        <RichTextEditor
          value={cmsData.homePage.ctaTicketsButton}
          onChange={(val) => handleInputChange('homePage', 'ctaTicketsButton', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderAboutPage = () => (
    <div className="cms-section-content">
      <h2>About Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.aboutPage.title}
          onChange={(val) => handleInputChange('aboutPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 1</label>
        <RichTextEditor
          value={cmsData.aboutPage.paragraph1}
          onChange={(val) => handleInputChange('aboutPage', 'paragraph1', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 2</label>
        <RichTextEditor
          value={cmsData.aboutPage.paragraph2}
          onChange={(val) => handleInputChange('aboutPage', 'paragraph2', val)}
        />
      </div>
    </div>
  );

  const renderWhatIsMenaPage = () => (
    <div className="cms-section-content">
      <h2>What is MENA Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.whatIsMenaPage.title}
          onChange={(val) => handleInputChange('whatIsMenaPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 1</label>
        <RichTextEditor
          value={cmsData.whatIsMenaPage.paragraph1}
          onChange={(val) => handleInputChange('whatIsMenaPage', 'paragraph1', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 2</label>
        <RichTextEditor
          value={cmsData.whatIsMenaPage.paragraph2}
          onChange={(val) => handleInputChange('whatIsMenaPage', 'paragraph2', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 3</label>
        <RichTextEditor
          value={cmsData.whatIsMenaPage.paragraph3}
          onChange={(val) => handleInputChange('whatIsMenaPage', 'paragraph3', val)}
        />
      </div>
    </div>
  );

  const handleFaqItemCommit = useCallback((sectionIndex, itemIndex, field, value) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [...newData.faqPage.sections];
      newData.faqPage.sections[sectionIndex] = { ...newData.faqPage.sections[sectionIndex] };
      newData.faqPage.sections[sectionIndex].items = [...newData.faqPage.sections[sectionIndex].items];
      newData.faqPage.sections[sectionIndex].items[itemIndex] = {
        ...newData.faqPage.sections[sectionIndex].items[itemIndex],
        [field]: value
      };
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleFaqSubheadingCommit = useCallback((sectionIndex, value) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [...newData.faqPage.sections];
      newData.faqPage.sections[sectionIndex] = { ...newData.faqPage.sections[sectionIndex], subheading: value };
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleAddFaqItem = useCallback((sectionIndex) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [...newData.faqPage.sections];
      newData.faqPage.sections[sectionIndex] = { ...newData.faqPage.sections[sectionIndex] };
      newData.faqPage.sections[sectionIndex].items = [
        ...newData.faqPage.sections[sectionIndex].items,
        { id: `faq-${Date.now()}`, question: '', answer: '' }
      ];
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleRemoveFaqItem = useCallback((sectionIndex, itemIndex) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [...newData.faqPage.sections];
      newData.faqPage.sections[sectionIndex] = { ...newData.faqPage.sections[sectionIndex] };
      newData.faqPage.sections[sectionIndex].items = newData.faqPage.sections[sectionIndex].items.filter((_, i) => i !== itemIndex);
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleMoveFaqItem = useCallback((sectionIndex, itemIndex, direction) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [...newData.faqPage.sections];
      newData.faqPage.sections[sectionIndex] = { ...newData.faqPage.sections[sectionIndex] };
      const items = [...newData.faqPage.sections[sectionIndex].items];
      const newIndex = itemIndex + direction;
      if (newIndex < 0 || newIndex >= items.length) return prev;
      [items[itemIndex], items[newIndex]] = [items[newIndex], items[itemIndex]];
      newData.faqPage.sections[sectionIndex].items = items;
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleAddFaqSection = useCallback(() => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = [
        ...newData.faqPage.sections,
        { subheading: 'New Section', items: [{ id: `faq-${Date.now()}`, question: '', answer: '' }] }
      ];
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleRemoveFaqSection = useCallback((sectionIndex) => {
    if (!window.confirm('Remove this entire FAQ section and all its items?')) return;
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      newData.faqPage.sections = newData.faqPage.sections.filter((_, i) => i !== sectionIndex);
      return newData;
    });
    setHasChanges(true);
  }, []);

  const handleMoveFaqSection = useCallback((sectionIndex, direction) => {
    setCmsData(prev => {
      const newData = { ...prev };
      newData.faqPage = { ...newData.faqPage };
      const sections = [...newData.faqPage.sections];
      const newIndex = sectionIndex + direction;
      if (newIndex < 0 || newIndex >= sections.length) return prev;
      [sections[sectionIndex], sections[newIndex]] = [sections[newIndex], sections[sectionIndex]];
      newData.faqPage.sections = sections;
      return newData;
    });
    setHasChanges(true);
  }, []);

  const renderFaqPage = () => (
    <div className="cms-section-content">
      <h2>FAQ Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <input
          type="text"
          value={cmsData.faqPage.title}
          onChange={(e) => handleInputChange('faqPage', 'title', e.target.value)}
        />
      </div>
      
      {cmsData.faqPage.sections.map((section, sectionIndex) => (
        <FaqSectionBlock
          key={sectionIndex}
          section={section}
          sectionIndex={sectionIndex}
          totalSections={cmsData.faqPage.sections.length}
          onSubheadingCommit={handleFaqSubheadingCommit}
          onItemCommit={handleFaqItemCommit}
          onAddItem={handleAddFaqItem}
          onRemoveItem={handleRemoveFaqItem}
          onMoveItem={handleMoveFaqItem}
          onRemoveSection={handleRemoveFaqSection}
          onMoveSection={handleMoveFaqSection}
        />
      ))}

      <button
        onClick={handleAddFaqSection}
        style={{ marginTop: '1rem', padding: '0.6rem 1.2rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600' }}
      >
        + Add Section
      </button>
    </div>
  );

  const renderAmphitheaterPage = () => (
    <div className="cms-section-content">
      <h2>Amphitheater Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.title}
          onChange={(val) => handleInputChange('amphitheaterPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Subtitle</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.subtitle}
          onChange={(val) => handleInputChange('amphitheaterPage', 'subtitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Day Select Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.daySelectLabel}
          onChange={(val) => handleInputChange('amphitheaterPage', 'daySelectLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Saturday Button Text</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.saturdayButton}
          onChange={(val) => handleInputChange('amphitheaterPage', 'saturdayButton', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Sunday Button Text</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.sundayButton}
          onChange={(val) => handleInputChange('amphitheaterPage', 'sundayButton', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Both Days Note</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.bothDaysNote}
          onChange={(val) => handleInputChange('amphitheaterPage', 'bothDaysNote', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Ticket Quantity Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.ticketQuantityLabel}
          onChange={(val) => handleInputChange('amphitheaterPage', 'ticketQuantityLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Selected Seats Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.selectedSeatsLabel}
          onChange={(val) => handleInputChange('amphitheaterPage', 'selectedSeatsLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>No Seats Selected Text</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.noSeatsSelected}
          onChange={(val) => handleInputChange('amphitheaterPage', 'noSeatsSelected', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Total Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.totalLabel}
          onChange={(val) => handleInputChange('amphitheaterPage', 'totalLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Add to Cart Button</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.addToCartButton}
          onChange={(val) => handleInputChange('amphitheaterPage', 'addToCartButton', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Adding to Cart Button</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.addingToCartButton}
          onChange={(val) => handleInputChange('amphitheaterPage', 'addingToCartButton', val)}
          minHeight="60px"
        />
      </div>
      
      <h3 className="cms-subsection-title">Legend</h3>
      <div className="cms-field-group">
        <label>Legend Title</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.legendTitle}
          onChange={(val) => handleInputChange('amphitheaterPage', 'legendTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Available Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.legendAvailable}
          onChange={(val) => handleInputChange('amphitheaterPage', 'legendAvailable', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Selected Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.legendSelected}
          onChange={(val) => handleInputChange('amphitheaterPage', 'legendSelected', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Sold Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.legendSold}
          onChange={(val) => handleInputChange('amphitheaterPage', 'legendSold', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Accessible Label</label>
        <RichTextEditor
          value={cmsData.amphitheaterPage.legendAccessible}
          onChange={(val) => handleInputChange('amphitheaterPage', 'legendAccessible', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderTicketsPage = () => (
    <div className="cms-section-content">
      <h2>Tickets Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.ticketsPage.title}
          onChange={(val) => handleInputChange('ticketsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Intro Paragraph 1</label>
        <RichTextEditor
          value={cmsData.ticketsPage.intro1}
          onChange={(val) => handleInputChange('ticketsPage', 'intro1', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Intro Paragraph 2</label>
        <RichTextEditor
          value={cmsData.ticketsPage.intro2}
          onChange={(val) => handleInputChange('ticketsPage', 'intro2', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Section Title</label>
        <RichTextEditor
          value={cmsData.ticketsPage.sectionTitle}
          onChange={(val) => handleInputChange('ticketsPage', 'sectionTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Parking Notice</label>
        <RichTextEditor
          value={cmsData.ticketsPage.parkingNotice}
          onChange={(val) => handleInputChange('ticketsPage', 'parkingNotice', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Rides Note</label>
        <RichTextEditor
          value={cmsData.ticketsPage.ridesNote}
          onChange={(val) => handleInputChange('ticketsPage', 'ridesNote', val)}
          minHeight="60px"
        />
      </div>
      
      <h3 className="cms-subsection-title">Ticket Options</h3>
      {cmsData.ticketsPage.ticketOptions.map((ticket, index) => (
        <div key={ticket.id} className="cms-ticket-item">
          <h4>{ticket.name}</h4>
          <div className="cms-field-group">
            <label>Name</label>
            <input
              type="text"
              value={ticket.name}
              onChange={(e) => handleArrayItemChange('ticketsPage', 'ticketOptions', index, 'name', e.target.value)}
            />
          </div>
          <div className="cms-field-group">
            <label>Description</label>
            <RichTextEditor
              value={ticket.description}
              onChange={(val) => handleArrayItemChange('ticketsPage', 'ticketOptions', index, 'description', val)}
              minHeight="60px"
            />
          </div>
          <div className="cms-field-group">
            <label>Savings Text</label>
            <RichTextEditor
              value={ticket.savings}
              onChange={(val) => handleArrayItemChange('ticketsPage', 'ticketOptions', index, 'savings', val)}
              minHeight="60px"
            />
          </div>
          <div className="cms-field-group">
            <label>Price ($)</label>
            <input
              type="number"
              value={ticket.price}
              onChange={(e) => handleArrayItemChange('ticketsPage', 'ticketOptions', index, 'price', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderContactPage = () => (
    <div className="cms-section-content">
      <h2>Contact Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.contactPage.title}
          onChange={(val) => handleInputChange('contactPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Subtitle</label>
        <RichTextEditor
          value={cmsData.contactPage.subtitle}
          onChange={(val) => handleInputChange('contactPage', 'subtitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Contact Email</label>
        <input
          type="email"
          value={cmsData.contactPage.email}
          onChange={(e) => handleInputChange('contactPage', 'email', e.target.value)}
        />
      </div>
      <div className="cms-field-group">
        <label>Success Message</label>
        <RichTextEditor
          value={cmsData.contactPage.successMessage}
          onChange={(val) => handleInputChange('contactPage', 'successMessage', val)}
          minHeight="60px"
        />
      </div>
      
      <h3 className="cms-subsection-title">Form Labels</h3>
      {Object.entries(cmsData.contactPage.formLabels).map(([key, value]) => (
        <div key={key} className="cms-field-group">
          <label>{key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</label>
          <input
            type="text"
            value={value}
            onChange={(e) => {
              const newLabels = { ...cmsData.contactPage.formLabels, [key]: e.target.value };
              handleInputChange('contactPage', 'formLabels', newLabels);
            }}
          />
        </div>
      ))}
    </div>
  );

  const renderFestivalInfoPage = () => (
    <div className="cms-section-content">
      <h2>Festival Info Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.title}
          onChange={(val) => handleInputChange('festivalInfoPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      
      <h3 className="cms-subsection-title">General Info Tab</h3>
      <div className="cms-field-group">
        <label>Section Title</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.generalInfo.title}
          onChange={(val) => {
            const newGeneralInfo = { ...cmsData.festivalInfoPage.generalInfo, title: val };
            handleInputChange('festivalInfoPage', 'generalInfo', newGeneralInfo);
          }}
          minHeight="60px"
        />
      </div>
      {cmsData.festivalInfoPage.generalInfo.items.map((item, index) => (
        <div key={index} className="cms-field-group">
          <label>Item {index + 1}</label>
          <RichTextEditor
            value={item}
            onChange={(val) => {
              const newItems = [...cmsData.festivalInfoPage.generalInfo.items];
              newItems[index] = val;
              const newGeneralInfo = { ...cmsData.festivalInfoPage.generalInfo, items: newItems };
              handleInputChange('festivalInfoPage', 'generalInfo', newGeneralInfo);
            }}
            minHeight="60px"
          />
        </div>
      ))}

      <h3 className="cms-subsection-title">Hours Tab</h3>
      <div className="cms-field-group">
        <label>Section Title</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.hours.title}
          onChange={(val) => {
            const newHours = { ...cmsData.festivalInfoPage.hours, title: val };
            handleInputChange('festivalInfoPage', 'hours', newHours);
          }}
          minHeight="60px"
        />
      </div>

      <h3 className="cms-subsection-title">Code of Conduct Tab</h3>
      <div className="cms-field-group">
        <label>Introduction</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.codeOfConduct.intro}
          onChange={(val) => {
            const newConduct = { ...cmsData.festivalInfoPage.codeOfConduct, intro: val };
            handleInputChange('festivalInfoPage', 'codeOfConduct', newConduct);
          }}
        />
      </div>
      <div className="cms-field-group">
        <label>Enforcement Policy</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.codeOfConduct.enforcement}
          onChange={(val) => {
            const newConduct = { ...cmsData.festivalInfoPage.codeOfConduct, enforcement: val };
            handleInputChange('festivalInfoPage', 'codeOfConduct', newConduct);
          }}
        />
      </div>
      <div className="cms-field-group">
        <label>Refund Policy</label>
        <RichTextEditor
          value={cmsData.festivalInfoPage.codeOfConduct.refundPolicy}
          onChange={(val) => {
            const newConduct = { ...cmsData.festivalInfoPage.codeOfConduct, refundPolicy: val };
            handleInputChange('festivalInfoPage', 'codeOfConduct', newConduct);
          }}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderHeader = () => (
    <div className="cms-section-content">
      <h2>Header / Navigation</h2>
      <div className="cms-field-group">
        <label>Get Tickets Button Text</label>
        <RichTextEditor
          value={cmsData.header?.getTicketsButton || ''}
          onChange={(val) => handleInputChange('header', 'getTicketsButton', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Cart Label</label>
        <RichTextEditor
          value={cmsData.header?.cartLabel || ''}
          onChange={(val) => handleInputChange('header', 'cartLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Account Label</label>
        <RichTextEditor
          value={cmsData.header?.accountLabel || ''}
          onChange={(val) => handleInputChange('header', 'accountLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Dashboard Label</label>
        <RichTextEditor
          value={cmsData.header?.dashboardLabel || ''}
          onChange={(val) => handleInputChange('header', 'dashboardLabel', val)}
          minHeight="60px"
        />
      </div>
      <h3 className="cms-subsection-title">Navigation Items</h3>
      {cmsData.header?.navItems?.map((item, index) => (
        <div key={index} className="cms-field-group">
          <label>Nav Item {index + 1} - Name</label>
          <input
            type="text"
            value={item.name}
            onChange={(e) => {
              const newItems = [...cmsData.header.navItems];
              newItems[index] = { ...newItems[index], name: e.target.value };
              handleInputChange('header', 'navItems', newItems);
            }}
          />
        </div>
      ))}
    </div>
  );

  const renderSponsorsPage = () => (
    <div className="cms-section-content">
      <h2>Sponsors Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.title || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Description</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.description || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'description', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Levels Title</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.levelsTitle || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'levelsTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Levels Description</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.levelsDescription || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'levelsDescription', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Contact Text</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.contactText || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'contactText', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>CTA Text</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.ctaText || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'ctaText', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>CTA Button Text</label>
        <RichTextEditor
          value={cmsData.sponsorsPage?.ctaButton || ''}
          onChange={(val) => handleInputChange('sponsorsPage', 'ctaButton', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderVendorsPage = () => (
    <div className="cms-section-content">
      <h2>Vendors Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.title || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 1</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.paragraph1 || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'paragraph1', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 2</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.paragraph2 || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'paragraph2', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 3</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.paragraph3 || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'paragraph3', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Paragraph 4</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.paragraph4 || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'paragraph4', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Vendor List Title</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.vendorListTitle || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'vendorListTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Vendor List Text</label>
        <RichTextEditor
          value={cmsData.vendorsPage?.vendorListText || ''}
          onChange={(val) => handleInputChange('vendorsPage', 'vendorListText', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderEventSchedulePage = () => (
    <div className="cms-section-content">
      <h2>Event Schedule Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.eventSchedulePage?.title || ''}
          onChange={(val) => handleInputChange('eventSchedulePage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Description</label>
        <RichTextEditor
          value={cmsData.eventSchedulePage?.description || ''}
          onChange={(val) => handleInputChange('eventSchedulePage', 'description', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Newsletter Heading</label>
        <RichTextEditor
          value={cmsData.eventSchedulePage?.newsletterHeading || ''}
          onChange={(val) => handleInputChange('eventSchedulePage', 'newsletterHeading', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Newsletter Success Message</label>
        <RichTextEditor
          value={cmsData.eventSchedulePage?.newsletterSuccess || ''}
          onChange={(val) => handleInputChange('eventSchedulePage', 'newsletterSuccess', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Newsletter Button Text</label>
        <RichTextEditor
          value={cmsData.eventSchedulePage?.newsletterButton || ''}
          onChange={(val) => handleInputChange('eventSchedulePage', 'newsletterButton', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderEventMapPage = () => (
    <div className="cms-section-content">
      <h2>Event Map Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.eventMapPage?.title || ''}
          onChange={(val) => handleInputChange('eventMapPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Description</label>
        <RichTextEditor
          value={cmsData.eventMapPage?.description || ''}
          onChange={(val) => handleInputChange('eventMapPage', 'description', val)}
        />
      </div>
    </div>
  );

  const renderMapsDirectionsPage = () => (
    <div className="cms-section-content">
      <h2>Maps & Directions Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.mapsDirectionsPage?.title || ''}
          onChange={(val) => handleInputChange('mapsDirectionsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Content</label>
        <RichTextEditor
          value={cmsData.mapsDirectionsPage?.content || ''}
          onChange={(val) => handleInputChange('mapsDirectionsPage', 'content', val)}
        />
      </div>
    </div>
  );

  const renderLoginPage = () => (
    <div className="cms-section-content">
      <h2>Login Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.loginPage?.title || ''}
          onChange={(val) => handleInputChange('loginPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Login Tab Label</label>
        <RichTextEditor
          value={cmsData.loginPage?.loginTab || ''}
          onChange={(val) => handleInputChange('loginPage', 'loginTab', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Register Tab Label</label>
        <RichTextEditor
          value={cmsData.loginPage?.registerTab || ''}
          onChange={(val) => handleInputChange('loginPage', 'registerTab', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Login Heading</label>
        <RichTextEditor
          value={cmsData.loginPage?.loginHeading || ''}
          onChange={(val) => handleInputChange('loginPage', 'loginHeading', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Email Label</label>
        <RichTextEditor
          value={cmsData.loginPage?.emailLabel || ''}
          onChange={(val) => handleInputChange('loginPage', 'emailLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Password Label</label>
        <RichTextEditor
          value={cmsData.loginPage?.passwordLabel || ''}
          onChange={(val) => handleInputChange('loginPage', 'passwordLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Remember Me Text</label>
        <RichTextEditor
          value={cmsData.loginPage?.rememberMe || ''}
          onChange={(val) => handleInputChange('loginPage', 'rememberMe', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Forgot Password Text</label>
        <RichTextEditor
          value={cmsData.loginPage?.forgotPassword || ''}
          onChange={(val) => handleInputChange('loginPage', 'forgotPassword', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Submit Button Text</label>
        <RichTextEditor
          value={cmsData.loginPage?.submitButton || ''}
          onChange={(val) => handleInputChange('loginPage', 'submitButton', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderSignupPage = () => (
    <div className="cms-section-content">
      <h2>Signup Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.signupPage?.title || ''}
          onChange={(val) => handleInputChange('signupPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Register Heading</label>
        <RichTextEditor
          value={cmsData.signupPage?.registerHeading || ''}
          onChange={(val) => handleInputChange('signupPage', 'registerHeading', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Email Label</label>
        <RichTextEditor
          value={cmsData.signupPage?.emailLabel || ''}
          onChange={(val) => handleInputChange('signupPage', 'emailLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Password Label</label>
        <RichTextEditor
          value={cmsData.signupPage?.passwordLabel || ''}
          onChange={(val) => handleInputChange('signupPage', 'passwordLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Privacy Notice</label>
        <RichTextEditor
          value={cmsData.signupPage?.privacyNotice || ''}
          onChange={(val) => handleInputChange('signupPage', 'privacyNotice', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Submit Button Text</label>
        <RichTextEditor
          value={cmsData.signupPage?.submitButton || ''}
          onChange={(val) => handleInputChange('signupPage', 'submitButton', val)}
          minHeight="60px"
        />
      </div>
    </div>
  );

  const renderLegalPages = () => (
    <div className="cms-section-content">
      <h2>Legal Pages</h2>
      
      <h3 className="cms-subsection-title">Privacy Policy</h3>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.privacyPolicyPage?.title || ''}
          onChange={(val) => handleInputChange('privacyPolicyPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Effective Date</label>
        <input
          type="text"
          value={cmsData.privacyPolicyPage?.effectiveDate || ''}
          onChange={(e) => handleInputChange('privacyPolicyPage', 'effectiveDate', e.target.value)}
        />
      </div>

      <h3 className="cms-subsection-title">CPRA Notice</h3>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.cpraNoticePage?.title || ''}
          onChange={(val) => handleInputChange('cpraNoticePage', 'title', val)}
          minHeight="60px"
        />
      </div>

      <h3 className="cms-subsection-title">Accessibility / ADA</h3>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.accessibilityPage?.title || ''}
          onChange={(val) => handleInputChange('accessibilityPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Content</label>
        <RichTextEditor
          value={cmsData.accessibilityPage?.content || ''}
          onChange={(val) => handleInputChange('accessibilityPage', 'content', val)}
        />
      </div>

      <h3 className="cms-subsection-title">Terms of Use</h3>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.termsOfUsePage?.title || ''}
          onChange={(val) => handleInputChange('termsOfUsePage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Effective Date</label>
        <input
          type="text"
          value={cmsData.termsOfUsePage?.effectiveDate || ''}
          onChange={(e) => handleInputChange('termsOfUsePage', 'effectiveDate', e.target.value)}
        />
      </div>

      <h3 className="cms-subsection-title">Ticket Terms</h3>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.ticketTermsPage?.title || ''}
          onChange={(val) => handleInputChange('ticketTermsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Effective Date</label>
        <input
          type="text"
          value={cmsData.ticketTermsPage?.effectiveDate || ''}
          onChange={(e) => handleInputChange('ticketTermsPage', 'effectiveDate', e.target.value)}
        />
      </div>
    </div>
  );

  const renderFooter = () => (
    <div className="cms-section-content">
      <h2>Footer Settings</h2>
      <div className="cms-field-group">
        <label>Newsletter Label</label>
        <RichTextEditor
          value={cmsData.footer.newsletterLabel}
          onChange={(val) => handleInputChange('footer', 'newsletterLabel', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Newsletter Text</label>
        <RichTextEditor
          value={cmsData.footer.newsletterText}
          onChange={(val) => handleInputChange('footer', 'newsletterText', val)}
          minHeight="60px"
        />
      </div>

      <h3 className="cms-subsection-title">Social Media Links</h3>
      {Object.entries(cmsData.footer.socialLinks).map(([platform, url]) => (
        <div key={platform} className="cms-field-group">
          <label>{platform.charAt(0).toUpperCase() + platform.slice(1)}</label>
          <input
            type="url"
            value={url}
            onChange={(e) => {
              const newSocialLinks = { ...cmsData.footer.socialLinks, [platform]: e.target.value };
              handleInputChange('footer', 'socialLinks', newSocialLinks);
            }}
          />
        </div>
      ))}
    </div>
  );

  const renderVendorBoothsPage = () => (
    <div className="cms-section-content">
      <h2>Vendor Booths Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.title || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Intro Paragraph 1</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.intro1 || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'intro1', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Intro Paragraph 2</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.intro2 || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'intro2', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Intro Paragraph 3</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.intro3 || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'intro3', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Select Booth Title</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.selectBoothTitle || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'selectBoothTitle', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Setup Terms Text</label>
        <RichTextEditor
          value={cmsData.vendorBoothsPage?.bazaarTerms || ''}
          onChange={(val) => handleInputChange('vendorBoothsPage', 'bazaarTerms', val)}
        />
      </div>

      <h3 className="cms-subsection-title">Booth Options</h3>
      {(cmsData.vendorBoothsPage?.boothOptions || []).map((booth, index) => (
        <div key={booth.id} className="cms-ticket-item">
          <h4>{booth.name}</h4>
          <div className="cms-field-group">
            <label>Name</label>
            <input
              type="text"
              value={booth.name}
              onChange={(e) => handleArrayItemChange('vendorBoothsPage', 'boothOptions', index, 'name', e.target.value)}
            />
          </div>
          <div className="cms-field-group">
            <label>Description</label>
            <RichTextEditor
              value={booth.description}
              onChange={(val) => handleArrayItemChange('vendorBoothsPage', 'boothOptions', index, 'description', val)}
              minHeight="60px"
            />
          </div>
          <div className="cms-field-group">
            <label>Price ($)</label>
            <input
              type="number"
              value={booth.price}
              onChange={(e) => handleArrayItemChange('vendorBoothsPage', 'boothOptions', index, 'price', parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
      ))}
    </div>
  );

  const renderSiteImages = () => (
    <div className="cms-section-content">
      <h2>Site Images</h2>
      <p style={{color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem'}}>
        Below are all images used across the website. To replace an image, upload a new file with the <strong>exact same filename</strong> to the <code>/public</code> folder, or update the path below to point to a new file.
      </p>
      
      {cmsData.siteImages && Object.entries(cmsData.siteImages).map(([key, img]) => (
        <div key={key} style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '0.75rem',
          borderBottom: '1px solid #e5e7eb',
          marginBottom: '0.5rem'
        }}>
          <div style={{
            width: '80px',
            height: '60px',
            borderRadius: '6px',
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#f9fafb'
          }}>
            {img.path?.endsWith('.mp4') ? (
              <span style={{fontSize: '1.5rem'}}>🎬</span>
            ) : (
              <img loading="lazy" 
                src={img.path} 
                alt={img.label} 
                style={{maxWidth: '100%', maxHeight: '100%', objectFit: 'contain'}}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
          </div>
          <div style={{flex: 1, minWidth: 0}}>
            <div style={{fontWeight: 600, fontSize: '0.9rem', color: '#333'}}>{img.label}</div>
            <div style={{fontSize: '0.78rem', color: '#888'}}>Used on: {img.usedOn}</div>
            <div className="cms-field-group" style={{marginBottom: 0, marginTop: '4px', display: 'flex', gap: '8px', alignItems: 'center'}}>
              <input
                type="text"
                value={img.path}
                onChange={(e) => {
                  const newImages = { ...cmsData.siteImages };
                  newImages[key] = { ...newImages[key], path: e.target.value };
                  handleInputChange('siteImages', key, newImages[key]);
                }}
                style={{fontSize: '0.82rem', padding: '4px 8px', flex: 1}}
              />
              {!img.path?.endsWith('.mp4') && (
                <button
                  type="button"
                  onClick={() => handleImageUpload('siteImages', key)}
                  disabled={uploadingImage}
                  style={{
                    padding: '4px 12px', backgroundColor: '#c41e3a', color: 'white',
                    border: 'none', borderRadius: '6px', fontSize: '0.78rem',
                    fontWeight: '600', cursor: 'pointer', whiteSpace: 'nowrap',
                    opacity: uploadingImage ? 0.6 : 1
                  }}
                >
                  Upload
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderMusicPage = () => (
    <div className="cms-section-content">
      <h2>Music Page</h2>
      <div className="cms-field-group">
        <label>Page Title</label>
        <RichTextEditor
          value={cmsData.musicPage?.title || ''}
          onChange={(val) => handleInputChange('musicPage', 'title', val)}
          minHeight="60px"
        />
      </div>
      <div className="cms-field-group">
        <label>Page Description</label>
        <RichTextEditor
          value={cmsData.musicPage?.description || ''}
          onChange={(val) => handleInputChange('musicPage', 'description', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Amphitheater Section Title</label>
        <input
          type="text"
          value={cmsData.musicPage?.amphitheaterTitle || ''}
          onChange={(e) => handleInputChange('musicPage', 'amphitheaterTitle', e.target.value)}
        />
      </div>
      <div className="cms-field-group">
        <label>Amphitheater Description</label>
        <RichTextEditor
          value={cmsData.musicPage?.amphitheaterDescription || ''}
          onChange={(val) => handleInputChange('musicPage', 'amphitheaterDescription', val)}
        />
      </div>
      <div className="cms-field-group">
        <label>Amphitheater Notice</label>
        <RichTextEditor
          value={cmsData.musicPage?.amphitheaterNotice || ''}
          onChange={(val) => handleInputChange('musicPage', 'amphitheaterNotice', val)}
        />
      </div>
    </div>
  );

  const seoPageLabels = {
    home: 'Home Page (/)',
    tickets: 'Tickets (/tickets)',
    festivalTickets: 'Festival Tickets (/festival-tickets)',
    amphitheaterTickets: 'Amphitheater Tickets (/amphitheater-tickets)',
    vendors: 'Vendors (/vendors)',
    bazaarVendor: 'Bazaar Vendor (/bazaar-vendor)',
    foodVendor: 'Food Vendor (/food-vendor)',
    about: 'About (/about)',
    whatIsMena: 'What is MENA (/what-is-mena)',
    faq: 'FAQ (/faq)',
    festivalInfo: 'Festival Info (/festival)',
    music: 'Music (/music)',
    sponsors: 'Sponsors (/sponsors)',
    sponsorsInfo: 'Sponsors Info (/sponsors-info)',
    contact: 'Contact (/contact)',
    eventSchedule: 'Event Schedule (/event-schedule)',
    eventMap: 'Event Map (/event-map)',
    mapsDirections: 'Maps & Directions (/maps-directions)',
    privacyPolicy: 'Privacy Policy (/privacy-policy)',
    termsOfUse: 'Terms of Use (/terms-of-use)',
    ticketTerms: 'Ticket Terms (/ticket-terms)',
  };

  const updateSeoField = (key, field, value) => {
    setCmsData(prev => ({
      ...prev,
      seoMetadata: {
        ...prev.seoMetadata,
        pages: {
          ...prev.seoMetadata?.pages,
          [key]: { ...prev.seoMetadata?.pages?.[key], [field]: value }
        }
      }
    }));
    setHasChanges(true);
  };

  const renderSeoMetadata = () => (
    <div className="cms-section-content">
      <h2>SEO Metadata & Share Images</h2>
      <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Manage per-page SEO: title, description, Open Graph, keywords, canonical URL, robots directive, and share image. Changes apply immediately after saving. Leave fields empty to use defaults.
      </p>

      <div style={{ marginBottom: '2rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bae6fd' }}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem' }}>Default Share Image (Fallback)</h3>
        <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '0.5rem' }}>Used when a page does not have its own share image set.</p>
        <div className="cms-field-group">
          <label>Default Share Image URL</label>
          <input
            type="text"
            value={cmsData.seoMetadata?.defaultShareImage || ''}
            onChange={(e) => {
              setCmsData(prev => ({
                ...prev,
                seoMetadata: {
                  ...prev.seoMetadata,
                  defaultShareImage: e.target.value,
                }
              }));
              setHasChanges(true);
            }}
            placeholder="/og-image.jpg or https://..."
          />
        </div>
        {cmsData.seoMetadata?.defaultShareImage && (
          <img
loading="lazy" 
            src={cmsData.seoMetadata.defaultShareImage}
            alt="Default share preview"
            style={{ maxWidth: '200px', maxHeight: '120px', marginTop: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        )}
      </div>

      {Object.entries(seoPageLabels).map(([key, label]) => {
        const pageMeta = cmsData.seoMetadata?.pages?.[key] || {};
        return (
          <div key={key} style={{ marginBottom: '2rem', padding: '1.25rem', background: '#fff', borderRadius: '10px', border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: '#1e293b', borderBottom: '1px solid #f1f5f9', paddingBottom: '0.5rem' }}>
              {label}
            </h3>

            <div className="cms-field-group">
              <label>Page Title <span style={{ color: '#999', fontWeight: 'normal' }}>(browser tab & search results)</span></label>
              <input
                type="text"
                value={pageMeta.title || ''}
                onChange={(e) => updateSeoField(key, 'title', e.target.value)}
                placeholder={`OC MENA Festival | ${label}`}
              />
            </div>

            <div className="cms-field-group">
              <label>Meta Description <span style={{ color: '#999', fontWeight: 'normal' }}>(search engines, ~155 chars)</span></label>
              <textarea
                value={pageMeta.description || ''}
                onChange={(e) => updateSeoField(key, 'description', e.target.value)}
                rows={2}
                placeholder="Page description shown in Google search results..."
              />
              {pageMeta.description && (
                <span style={{ fontSize: '0.75rem', color: pageMeta.description.length > 160 ? '#dc2626' : '#6b7280', marginTop: '2px' }}>
                  {pageMeta.description.length}/160 characters
                </span>
              )}
            </div>

            <div className="cms-field-group">
              <label>Keywords <span style={{ color: '#999', fontWeight: 'normal' }}>(comma-separated)</span></label>
              <input
                type="text"
                value={pageMeta.keywords || ''}
                onChange={(e) => updateSeoField(key, 'keywords', e.target.value)}
                placeholder="OC MENA Festival, Orange County, Costa Mesa..."
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div className="cms-field-group">
                <label>OG Title <span style={{ color: '#999', fontWeight: 'normal' }}>(social sharing)</span></label>
                <input
                  type="text"
                  value={pageMeta.ogTitle || ''}
                  onChange={(e) => updateSeoField(key, 'ogTitle', e.target.value)}
                  placeholder="Defaults to Page Title if empty"
                />
              </div>
              <div className="cms-field-group">
                <label>OG Description <span style={{ color: '#999', fontWeight: 'normal' }}>(social sharing)</span></label>
                <input
                  type="text"
                  value={pageMeta.ogDescription || ''}
                  onChange={(e) => updateSeoField(key, 'ogDescription', e.target.value)}
                  placeholder="Defaults to Meta Description if empty"
                />
              </div>
            </div>

            <div className="cms-field-group">
              <label>Share Image URL <span style={{ color: '#999', fontWeight: 'normal' }}>(OG & Twitter image, leave empty to use default)</span></label>
              <input
                type="text"
                value={pageMeta.shareImage || ''}
                onChange={(e) => updateSeoField(key, 'shareImage', e.target.value)}
                placeholder="/og-image.jpg or https://..."
              />
            </div>
            {pageMeta.shareImage && (
              <img
loading="lazy" 
                src={pageMeta.shareImage}
                alt={`${label} share preview`}
                style={{ maxWidth: '200px', maxHeight: '120px', marginTop: '0.5rem', borderRadius: '6px', border: '1px solid #ddd' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.5rem' }}>
              <div className="cms-field-group">
                <label>Canonical URL <span style={{ color: '#999', fontWeight: 'normal' }}>(override, leave empty for auto)</span></label>
                <input
                  type="text"
                  value={pageMeta.canonical || ''}
                  onChange={(e) => updateSeoField(key, 'canonical', e.target.value)}
                  placeholder="https://www.ocmenafestival.com/..."
                />
              </div>
              <div className="cms-field-group">
                <label>Robots <span style={{ color: '#999', fontWeight: 'normal' }}>(leave empty for index,follow)</span></label>
                <select
                  value={pageMeta.robots || ''}
                  onChange={(e) => updateSeoField(key, 'robots', e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '0.9rem' }}
                >
                  <option value="">Default (index, follow)</option>
                  <option value="noindex, nofollow">noindex, nofollow</option>
                  <option value="noindex, follow">noindex, follow</option>
                  <option value="index, nofollow">index, nofollow</option>
                </select>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderCustomCSS = () => (
    <div className="cms-section-content">
      <h2>Custom CSS</h2>
      <p style={{ color: '#666', marginBottom: '1rem', fontSize: '0.9rem' }}>
        Add custom CSS rules to override site styling. Changes apply globally after saving.
      </p>
      <div className="cms-field-group">
        <label>CSS Overrides</label>
        <textarea
          value={cmsData.customCSS || ''}
          onChange={(e) => {
            setCmsData(prev => ({ ...prev, customCSS: e.target.value }));
            setHasChanges(true);
          }}
          rows={20}
          style={{ fontFamily: 'monospace', fontSize: '0.85rem', lineHeight: '1.5', whiteSpace: 'pre' }}
          placeholder={`/* Example:\n.hero-title {\n  color: #c41e3a;\n  font-size: 3rem;\n}\n*/`}
        />
      </div>
    </div>
  );

  const renderSectionContent = () => {
    switch (activeSection) {
      case 'global':
        return renderGlobalSettings();
      case 'header':
        return renderHeader();
      case 'homePage':
        return renderHomePage();
      case 'aboutPage':
        return renderAboutPage();
      case 'whatIsMenaPage':
        return renderWhatIsMenaPage();
      case 'faqPage':
        return renderFaqPage();
      case 'ticketsPage':
        return renderTicketsPage();
      case 'amphitheaterPage':
        return renderAmphitheaterPage();
      case 'contactPage':
        return renderContactPage();
      case 'festivalInfoPage':
        return renderFestivalInfoPage();
      case 'sponsorsPage':
        return renderSponsorsPage();
      case 'vendorsPage':
        return renderVendorsPage();
      case 'eventSchedulePage':
        return renderEventSchedulePage();
      case 'eventMapPage':
        return renderEventMapPage();
      case 'mapsDirectionsPage':
        return renderMapsDirectionsPage();
      case 'loginPage':
        return renderLoginPage();
      case 'signupPage':
        return renderSignupPage();
      case 'vendorBoothsPage':
        return renderVendorBoothsPage();
      case 'musicPage':
        return renderMusicPage();
      case 'legalPages':
        return renderLegalPages();
      case 'footer':
        return renderFooter();
      case 'siteImages':
        return renderSiteImages();
      case 'seoMetadata':
        return renderSeoMetadata();
      case 'customCSS':
        return renderCustomCSS();
      default:
        return renderGlobalSettings();
    }
  };

  return (
    <div className="cms-dashboard">
      {/* Hidden file input for image uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        onChange={handleFileSelected}
      />
      {/* Sidebar */}
      <aside className="cms-sidebar">
        <div className="cms-sidebar-header">
          <img loading="lazy" src={getCmsImagePath("logo", "/logo.webp")} alt="oc mena festival" className="cms-sidebar-logo" />
          <h2>CMS Dashboard</h2>
        </div>
        
        <nav className="cms-nav">
          {sections.map(section => (
            <button
              key={section.id}
              className={`cms-nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="cms-nav-icon">{section.icon}</span>
              <span className="cms-nav-label">{section.label}</span>
            </button>
          ))}
        </nav>

        <div className="cms-sidebar-footer">
          <button className="cms-logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="cms-main">
        <header className="cms-header">
          <h1>Content Management</h1>
          <div className="cms-header-actions">
            {saveStatus && (
              <span className={`cms-save-status ${saveStatus.includes('success') ? 'success' : ''}`}>
                {saveStatus}
              </span>
            )}
            <button className="cms-reset-btn" onClick={handleReset}>
              Reset to Defaults
            </button>
            <button 
              className={`cms-save-btn ${hasChanges ? 'has-changes' : ''}`} 
              onClick={handleSave}
              disabled={!hasChanges}
            >
              {hasChanges ? 'Save Changes' : 'Saved'}
            </button>
          </div>
        </header>

        <div className="cms-content">
          {renderSectionContent()}
        </div>
      </main>
    </div>
  );
};

export default CmsDashboard;
